import { Calendar, X } from "lucide-react";
import { getTypeLabel } from "../../utils/helpers";

/**
 * Detail + status-update modal for Assigned-Escalations tab.
 *
 * Props:
 *   escalation       {object|null}
 *   onClose          {function}
 *   onUpdateStatus   {function}  — (escalationId, newStatus) => void
 *   loading          {boolean}
 *   theme            {string}
 *   screenSize       {string}
 *   styles           {object}
 *   spacing          {object}
 *   fonts            {object}
 *   language         {"en"|"ar"}
 *   t                {object}
 *   StatusBadge      {component}
 */
const DetailModal = ({
  escalation,
  onClose,
  onUpdateStatus,
  loading,
  theme,
  screenSize,
  styles,
  spacing,
  fonts,
  language,
  t,
  StatusBadge,
}) => {
  if (!escalation) return null;

  const readonlyBox = {
    ...styles.formInput,
    background:
      theme === "dark" ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.02)",
    padding: "1rem 1.25rem",
    display: "flex",
    alignItems: "center",
    cursor: "default",
  };

  const statusActions = [
    {
      status: "IN_PROGRESS",
      label: t.mark_in_progress,
      activeColor: "rgba(59,130,246,0.3)",
      gradient: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
    },
    {
      status: "RESOLVED",
      label: t.mark_resolved,
      activeColor: "rgba(16,185,129,0.3)",
      gradient: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
    },
    {
      status: "REJECTED",
      label: t.mark_rejected,
      activeColor: "rgba(239,68,68,0.3)",
      gradient: "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)",
    },
  ];

  return (
    <div style={styles.modal} onClick={onClose}>
      <div
        style={{ ...styles.modalContent, maxWidth: "650px" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={styles.modalHeader}>
          <h2 style={styles.modalTitle}>{t.escalationDetails}</h2>
          <button style={styles.closeBtn} onClick={onClose} title={t.close}>
            <X size={screenSize === "xs" ? 20 : 24} />
          </button>
        </div>

        {/* Escalation Info */}
        <div style={styles.sectionTitle}>{t.escalationDetails}</div>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{t.escalationId}</label>
            <div style={{ ...readonlyBox, color: "#2e7cba", fontWeight: "600" }}>
              ESC-{escalation.id}
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{t.detail_type}</label>
            <div style={readonlyBox}>
              {getTypeLabel(escalation.type, language)}
            </div>
          </div>
        </div>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{t.detail_status}</label>
            <div style={{ ...readonlyBox, padding: "0.75rem 1.25rem", minHeight: "44px" }}>
              <StatusBadge status={escalation.status} />
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{t.detail_group}</label>
            <div style={readonlyBox}>{escalation.groupName}</div>
          </div>
        </div>

        {/* Issuer Info */}
        <div style={styles.sectionTitle}>{t.detail_issuer_sr}</div>

        <div style={styles.formRow}>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{t.detail_issuer_sr}</label>
            <div style={readonlyBox}>
              {escalation.issuerEmployee?.sr || t.na}
            </div>
          </div>
          <div style={styles.formGroup}>
            <label style={styles.formLabel}>{t.detail_issuer_name_ar}</label>
            <div style={readonlyBox}>
              {escalation.issuerEmployee?.arabicName || t.na}
            </div>
          </div>
        </div>

        <div style={styles.formGroup}>
          <label style={styles.formLabel}>{t.detail_issued_at}</label>
          <div style={{ ...readonlyBox, gap: "0.5rem" }}>
            <Calendar size={14} style={{ opacity: 0.6 }} />
            {new Date(escalation.issuedAt).toLocaleString()}
          </div>
        </div>

        {/* Description */}
        <div style={styles.sectionTitle}>{t.detail_description}</div>
        <div style={styles.formGroup}>
          <div
            style={{
              ...styles.formTextarea,
              minHeight: "120px",
              padding: "1.25rem",
              background:
                theme === "dark"
                  ? "rgba(255,255,255,0.05)"
                  : "rgba(0,0,0,0.02)",
              border: "none",
              cursor: "default",
              resize: "none",
            }}
          >
            {escalation.description || t.no_description_provided}
          </div>
        </div>

        {/* Update Status */}
        <div style={styles.sectionTitle}>{t.update_status}</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              screenSize === "xs" ? "1fr" : "repeat(3, 1fr)",
            gap: "0.75rem",
            marginBottom: spacing.margin,
          }}
        >
          {statusActions.map(({ status, label, activeColor, gradient }) => {
            const isActive = escalation.status === status;
            return (
              <button
                key={status}
                onClick={() => onUpdateStatus(escalation.id, status)}
                disabled={loading || isActive}
                style={{
                  background: isActive ? activeColor : gradient,
                  color: "white",
                  border: "none",
                  padding: "0.875rem 1rem",
                  borderRadius: "0.75rem",
                  cursor: isActive || loading ? "not-allowed" : "pointer",
                  fontSize: fonts.body,
                  fontWeight: "600",
                  opacity: isActive || loading ? 0.5 : 1,
                  transition: "all 0.3s ease",
                }}
              >
                {label}
              </button>
            );
          })}
        </div>

        {/* Close */}
        <div style={styles.modalActions}>
          <button
            onClick={onClose}
            style={{
              ...styles.submitBtn,
              minWidth: screenSize === "xs" ? "100%" : "150px",
            }}
          >
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DetailModal;